import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    ALL_LINT_CHECKS,
    type LintCheckName,
    type LintFinding,
    type LintReport,
    runVideoLint,
} from '../lint/videoLinter';
import type { ToolExecutionContext } from '../types';

import { reviewSkippedResult } from './review-skipping';
import { computeSceneShortIds } from './scene-addressing';
import { computeSceneGroupShortIds } from './scene-group-addressing';
import { Tool, type ToolConstructorArgs } from './Tool';

import {
    clearAllSceneBuilding,
    enterCreationStage,
    isReviewSkipped,
} from 'js/src/data/agent-activity';

export const lintVideoSchema = z.object({
    check_name: z
        .enum([...ALL_LINT_CHECKS] as [LintCheckName, ...LintCheckName[]])
        .optional()
        .describe('Optional single check to run. Omit to run every check.'),
});
export type LintVideoArgs = z.infer<typeof lintVideoSchema>;

/**
 * How the hosting agent's edit tools address a scene, and therefore how the lint report should point
 * at one. The toolbox tools (set_text/set_media/set_icon) take a stable scene_id; the legacy chat
 * tools take a positional scene_index. Findings always carry the stable scene id, and the tool
 * renders whichever locator matches the agent it runs under.
 */
export type SceneAddressing = 'index' | 'scene_id';

export interface LintVideoConstructorArgs extends ToolConstructorArgs {
    sceneAddressing: SceneAddressing;
}

/** Renders the scene portion of a finding's location from its stable scene id. */
type SceneLocatorFormatter = (sceneId: string) => string;

/** Renders the scene-group portion of a finding's location from its scene-group index. */
type GroupLocatorFormatter = (sceneGroupIndex: number) => string;

/**
 * The location prefix shown before a finding's message. Each part is labelled with the same locator
 * the hosting agent's edit tools accept: a scene-level finding via `formatSceneLocator` (a positional
 * index for legacy agents, a short scene_id for the toolbox), and a scene-group-level finding via
 * `formatGroupLocator` (a positional index for legacy agents, a short scene_group_id for the toolbox).
 */
function formatLocation(
    finding: LintFinding,
    formatSceneLocator: SceneLocatorFormatter,
    formatGroupLocator: GroupLocatorFormatter,
): string {
    const parts: string[] = [];
    if (finding.sceneGroupIndex !== undefined) {
        parts.push(formatGroupLocator(finding.sceneGroupIndex));
    }
    if (finding.sceneId !== undefined) {
        const sceneIds = Array.isArray(finding.sceneId) ? finding.sceneId : [finding.sceneId];
        parts.push(...sceneIds.map((sceneId) => formatSceneLocator(sceneId)));
    }
    return parts.length > 0 ? `[${parts.join(', ')}] ` : '';
}

function formatFinding(
    finding: LintFinding,
    formatSceneLocator: SceneLocatorFormatter,
    formatGroupLocator: GroupLocatorFormatter,
): string {
    return `  - ${formatLocation(finding, formatSceneLocator, formatGroupLocator)}${finding.message} (${finding.check})`;
}

export function formatLintReport(
    report: LintReport,
    formatSceneLocator: SceneLocatorFormatter = (sceneId) => `scene_id ${sceneId}`,
    formatGroupLocator: GroupLocatorFormatter = (sceneGroupIndex) =>
        `scene group ${sceneGroupIndex}`,
): string {
    const errors = report.findings.filter((finding) => finding.severity === 'error');
    const warnings = report.findings.filter((finding) => finding.severity === 'warning');

    if (report.findings.length === 0) {
        return `Video passes all checks (${report.checksRun.length} check(s) run): no errors or warnings.`;
    }

    const lines: string[] = [
        `Lint found ${errors.length} error(s) and ${warnings.length} warning(s).`,
    ];

    if (errors.length > 0) {
        lines.push('', 'Errors (fix these before finishing):');
        errors.forEach((finding) =>
            lines.push(formatFinding(finding, formatSceneLocator, formatGroupLocator)),
        );
    }

    if (warnings.length > 0) {
        lines.push('', 'Warnings (confirm each is intentional before finishing):');
        warnings.forEach((finding) =>
            lines.push(formatFinding(finding, formatSceneLocator, formatGroupLocator)),
        );
    }

    return lines.join('\n');
}

/**
 * Read-only tool that runs the combined video linter (overlay coverage,
 * caption overlap, text contrast, text clipping, placeholder text, scene
 * reading time) against the bound Luminary and returns one report with errors
 * and warnings clearly separated. Does not modify the video.
 */
export class LintVideoTool extends Tool<typeof lintVideoSchema> {
    protected override readonly schema = lintVideoSchema;
    protected override readonly toolName = TOOL_NAMES.lintVideo;
    protected override readonly toolDescription =
        'Run a battery of read-only quality checks on the current video and return one list of findings split into errors and warnings. Errors are hard visual defects you should fix (uncovered voiceover ranges, captions overlapping text, low text contrast, text being clipped). Warnings are "probably wrong but maybe intentional" (untouched placeholder text or icons, duplicated text on one scene, a line wrapping to a single stranded word, scenes too short to read comfortably) that you should confirm with the user, plus opportunities you are free to weigh and decline on your own. Read-only: does not modify the video.';

    private readonly sceneAddressing: SceneAddressing;

    public constructor(args: LintVideoConstructorArgs) {
        const { sceneAddressing, ...baseArgs } = args;
        super(baseArgs as ToolConstructorArgs);
        this.sceneAddressing = sceneAddressing;
    }

    public override getToolDisplayName(): string {
        return 'Checking video for issues';
    }

    public get statusIcon(): string {
        return 'rule';
    }

    public async execute(args: LintVideoArgs, _context: ToolExecutionContext) {
        // Linting means the build is essentially done, so nothing should still read as
        // pending through the final quality pass.
        this.dispatch(clearAllSceneBuilding());

        if (isReviewSkipped(this.getState())) {
            return reviewSkippedResult();
        }

        // The review phase often opens with a lint rather than an image_ask, so the stage
        // has to be announced from here too or the phase would go unlabelled until the
        // first vision call.
        this.dispatch(enterCreationStage({ stage: 'reviewing' }));

        // Deliberately no per-scene reviewing indicator here, unlike image_ask: lint is
        // fast enough that showing one only flashes, which reads as a glitch rather than
        // as feedback.
        const report = await runVideoLint(
            this.luminary,
            this.getState,
            args.check_name ? { only: [args.check_name] } : undefined,
        );
        return formatLintReport(
            report,
            this.buildSceneLocatorFormatter(),
            this.buildGroupLocatorFormatter(),
        );
    }

    /**
     * Build the scene-locator renderer for the hosting agent's addressing mode. In scene_id mode
     * each id is shortened to the same handle shown in the video state; in index mode the id is
     * resolved back to its current position — the scene_index the legacy edit tools take.
     */
    private buildSceneLocatorFormatter(): SceneLocatorFormatter {
        const scenes = this.luminary.scenes;
        if (this.sceneAddressing === 'index') {
            return (sceneId) => `scene ${scenes.findIndex((scene) => scene.id === sceneId)}`;
        }
        const shortSceneIds = computeSceneShortIds(scenes.map((scene) => scene.id));
        return (sceneId) => `scene_id ${shortSceneIds.get(sceneId) ?? sceneId}`;
    }

    /**
     * Build the scene-group-locator renderer for the hosting agent's addressing mode. In scene_id
     * mode a group is shown by the same short scene_group_id the group tools take; in index mode by
     * its positional index (which the legacy agents still address groups by).
     */
    private buildGroupLocatorFormatter(): GroupLocatorFormatter {
        const sceneGroups = this.luminary.sceneGroups;
        if (this.sceneAddressing === 'index') {
            return (sceneGroupIndex) => `scene group ${sceneGroupIndex}`;
        }
        const shortGroupIds = computeSceneGroupShortIds(sceneGroups.map((group) => group.id));
        return (sceneGroupIndex) => {
            const id = sceneGroups[sceneGroupIndex]?.id;
            return id
                ? `scene group ${shortGroupIds.get(id) ?? id}`
                : `scene group ${sceneGroupIndex}`;
        };
    }
}
